import { useState } from 'react';

import { updateAdminUser } from '@/services/admin';

import type { IAdminUser, IAdminUserUpdate } from '@/types/user';

type EditState = {
  values: Partial<IAdminUserUpdate>;
  editingField: keyof IAdminUserUpdate | null;
  saving: boolean;
  error: string | null;
};

const EDIT_STATE_DEFAULT: EditState = {
  values: {},
  editingField: null,
  saving: false,
  error: null,
};

function getFieldValueFromUser(
  user: IAdminUser | null | undefined,
  field: keyof IAdminUserUpdate,
): string {
  if (!user) return '';
  const value = user[field as keyof IAdminUser];
  return typeof value === 'string' ? value : '';
}

export function useAdminUserEdit(
  userId: string | undefined,
  effectiveUser: IAdminUser | null | undefined,
  refetch: () => Promise<unknown>,
) {
  const [editState, setEditState] = useState<EditState>(EDIT_STATE_DEFAULT);

  const handleEdit = (field: keyof IAdminUserUpdate) => {
    setEditState({
      ...EDIT_STATE_DEFAULT,
      editingField: field,
      values: { [field]: getFieldValueFromUser(effectiveUser, field) },
    });
  };

  const handleChange = (field: keyof IAdminUserUpdate, value: string) => {
    setEditState((current) => ({
      ...current,
      values: { ...current.values, [field]: value },
    }));
  };

  const handleSave = async () => {
    const { editingField, values } = editState;
    if (!userId || !editingField) return;

    setEditState((current) => ({ ...current, saving: true, error: null }));
    try {
      await updateAdminUser(userId, { [editingField]: values[editingField] });
      await refetch();
      setEditState(EDIT_STATE_DEFAULT);
    } catch {
      setEditState((current) => ({
        ...current,
        saving: false,
        error: 'Failed to save changes',
      }));
    }
  };

  const handleCancel = () => {
    setEditState(EDIT_STATE_DEFAULT);
  };

  return {
    editState,
    handleEdit,
    handleChange,
    handleSave,
    handleCancel,
  };
}
